import { X, Link, Tag } from '@phosphor-icons/react';
import { Assigned } from '@Models/types';
import DOMPurify from 'dompurify';
import { JIRA_SANITIZE_OPTIONS } from '@Lib/sanitize';
import JiraAttachmentList from '@Common/JiraAttachmentList';

interface JiraItemModalProps {
  isOpen: boolean;
  onCancel: () => void;
  record: Assigned | null;
  onTag: (record: Assigned) => void;
}

const statusColorMap: Record<string, string> = {
  'blue-gray': 'bg-gray-200 text-gray-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  green: 'bg-green-100 text-green-700',
  brown: 'bg-amber-100 text-amber-700',
  'warm-red': 'bg-red-100 text-red-700',
  blue: 'bg-blue-100 text-blue-700',
  red: 'bg-red-100 text-red-700',
  orange: 'bg-orange-100 text-orange-700',
  purple: 'bg-purple-100 text-purple-700',
  'medium-gray': 'bg-gray-200 text-gray-700',
  'light-gray': 'bg-gray-100 text-gray-600',
  'dark-gray': 'bg-gray-300 text-gray-800',
};

const JiraItemModal = ({
  isOpen,
  onCancel,
  record,
  onTag,
}: JiraItemModalProps) => {
  if (!record) return null;

  const statusColorClass =
    statusColorMap[record.workflow_status?.color?.toLowerCase() ?? ''] ??
    'bg-gray-200 text-gray-700';

  const tags =
    record.tags
      ?.map((t) => (typeof t === 'string' ? t : t.name))
      .filter(Boolean) ?? [];

  // Pull the HTML description carried through normalizeJiraIssue as an
  // extension field.  Falls back to record.description (plain text) so the
  // modal still renders something if htmlDescription is absent.
  const htmlDescription =
    (record as Assigned & { htmlDescription?: string }).htmlDescription ??
    record.description ??
    '';

  // Pull the attachment list that was carried through normalizeJiraIssue.
  // JiraAssigned uses `attachment` (singular); we read it via an extension
  // cast because the shared Assigned type does not declare the field.
  const attachments =
    (
      record as Assigned & {
        attachment?: {
          id: string;
          filename: string;
          mimeType: string;
          content: string;
        }[];
      }
    ).attachment ?? [];

  return (
    <div
      className={`${
        isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
      } fixed inset-0 z-50 flex h-full w-full items-center justify-center bg-gray-100 bg-opacity-70 transition-opacity duration-300`}
    >
      <div
        className={`flex w-1/2 transform flex-col rounded-xl bg-white pb-12 shadow-xl transition-transform duration-300 ${
          isOpen ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}
      >
        {/* Header */}
        <div className="flex h-12 items-center justify-between rounded-tl-lg rounded-tr-lg bg-primary-700 px-4 py-3">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-white">{record.reference_num}</p>
            {record.record_type && (
              <span className="rounded bg-primary-500 px-2 py-0.5 text-xs font-medium text-white">
                {record.record_type}
              </span>
            )}
          </div>
          <X
            onClick={onCancel}
            size={20}
            className="cursor-pointer text-white hover:text-red-500"
          />
        </div>

        {/* Body */}
        <div className="relative flex max-h-[70vh] flex-col gap-4 overflow-y-auto overflow-x-hidden rounded-bl-lg rounded-br-lg bg-white font-inter text-primary-700">
          <div className="custom-scroll overflow-x-auto pl-5 pr-5 pt-4">
            {/* Title */}
            <div className="mb-4">
              <p className="pb-1">
                <span className="font-semibold">Title: </span>
                <a
                  href={record.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-normal hover:text-primary-500 hover:underline"
                  title={record.url}
                >
                  {record.name}
                  <span className="relative ml-2">
                    <span className="absolute">
                      <Link size={21} />
                    </span>
                  </span>
                </a>
              </p>
            </div>

            {/* Status + Due Date row */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {record.status && (
                <span
                  className={`rounded-full px-3 py-0.5 text-xs font-semibold ${statusColorClass}`}
                >
                  {record.status}
                </span>
              )}
              {record.due_date && (
                <span className="text-sm text-gray-500">
                  Due:{' '}
                  <span className="font-medium text-gray-700">
                    {new Date(record.due_date).toLocaleDateString()}
                  </span>
                </span>
              )}
              {record.created_at && (
                <span className="text-sm text-gray-500">
                  Created:{' '}
                  <span className="font-medium text-gray-700">
                    {new Date(record.created_at).toLocaleString()}
                  </span>
                </span>
              )}
            </div>

            {/* Labels / Tags */}
            {tags.length > 0 && (
              <div className="mb-4">
                <p className="mb-1 flex items-center gap-1 font-semibold">
                  <Tag size={15} />
                  Labels
                </p>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, i) => (
                    <span
                      key={i}
                      className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <div className="mb-4">
              <p className="mb-1 text-lg font-semibold">Description</p>
              <div
                className="adf-body text-sm leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html:
                    DOMPurify.sanitize(
                      htmlDescription,
                      JIRA_SANITIZE_OPTIONS
                    ) ||
                    '<p class="text-gray-400 italic">No description available</p>',
                }}
              />
            </div>

            {/* Attachments */}
            <JiraAttachmentList attachments={attachments} />

            {/* AI Classification */}
            {record.gptClassification && (
              <div className="mb-4">
                <p className="mb-2 font-semibold">AI Classification</p>
                <div className="rounded-md border border-primary-100 bg-primary-50 px-4 py-3 text-sm">
                  <div className="mb-2 flex flex-wrap gap-3">
                    <span className="flex items-center gap-1">
                      <span className="text-xs font-bold uppercase tracking-wide text-blue-500">
                        Confidence
                      </span>
                      <span className="ml-1 rounded-full bg-primary-100 px-2.5 py-0.5 font-semibold text-primary-700">
                        {record.gptClassification.confidence_score}
                      </span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                        Production Bug
                      </span>
                      <span
                        className={`ml-1 rounded-full px-2.5 py-0.5 font-semibold ${
                          String(
                            record.gptClassification.production_bug
                          ).toLowerCase() === 'yes'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-200 text-green-800'
                        }`}
                      >
                        {String(
                          record.gptClassification.production_bug
                        ).toLowerCase() === 'yes'
                          ? 'Yes'
                          : 'No'}
                      </span>
                    </span>
                    {record.gptClassification.error && (
                      <span className="flex items-center gap-1">
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Status
                        </span>
                        <span className="ml-1 rounded-full bg-yellow-100 px-2.5 py-0.5 font-semibold text-yellow-700">
                          {record.gptClassification.error}
                        </span>
                      </span>
                    )}
                  </div>
                  {record.gptClassification.explanation && (
                    <div className="border-t border-primary-100 pt-2 leading-relaxed text-gray-600">
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Explanation
                      </span>
                      <p className="mt-1">
                        {record.gptClassification.explanation}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tag button */}
            <div className="fixed bottom-4 right-4 flex">
              <button
                onClick={() => onTag(record)}
                className="flex w-10 items-center justify-center rounded-md bg-primary-100 px-8 py-1 text-center text-sm font-semibold text-primary-700 opacity-50 hover:opacity-100"
              >
                Tag
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JiraItemModal;
